/**
 * Session transport. One socket per session, opened only once assistance is active.
 *
 * Deliberately dumb: it does not know what a page model is. It reconnects with backoff, and
 * gives up cleanly rather than retrying forever, because a session that cannot reach the
 * service must end rather than appear to be working.
 */
/**
 * Inbound frames, as the guidance service actually emits them. Note the nested `annotation`:
 * a highlight and a clear are BOTH t:"annotate", distinguished by annotation.kind.
 */
export type Annotation =
  | { kind: "highlightEl"; label?: string; sel?: string; highlightId?: string }
  /**
   * Ring several controls at once. A journey step that is a CHOICE — pick a card, pick a
   * bank — must show the whole set, otherwise highlighting one of six silently steers the
   * customer. The service sends the set; it still decides which, and the agent never does.
   */
  | { kind: "highlightMany"; labels: string[]; highlightId?: string }
  | { kind: "clear" };

export type Inbound =
  | { t: "annotate"; annotation: Annotation }
  | { t: "done" }
  | { t: "error"; message: string }
  | { t: "pong" };

export interface TransportEvents {
  onMessage: (m: Inbound) => void;
  onOpen: () => void;
  /** An established connection dropped. Not fatal — a retry is already scheduled. */
  onDrop?: () => void;
  onFail: (why: "network" | "rejected") => void;
}

/**
 * How hard to try before declaring the session unreachable.
 *
 * Sized for a phone call, not a page load. When the visual channel drops the customer is
 * usually still talking on a cellular voice channel that failed independently of their
 * data — a lift, a tunnel, a train — and giving up after thirty seconds ends a session
 * that was about to come back. Eight attempts with the ceiling below is a bit over two
 * minutes of patience.
 */
const MAX_ATTEMPTS = 8;
const MAX_BACKOFF_MS = 20_000;
const PING_MS = 25_000;

export class Transport {
  private ws?: WebSocket;
  private attempts = 0;
  private closed = false;
  private ping = 0;
  /**
   * Whether to present the token as a subprotocol. Starts true and flips permanently to
   * false the first time a handshake fails, because a server that predates subprotocol
   * auth ignores the offer, names no protocol in its 101, and the browser then tears the
   * connection down. A self-hosted deployment can be older than the SDK in front of it,
   * so we degrade to the query string rather than refusing to work.
   */
  private subprotocol = true;

  constructor(
    private endpoint: string,
    private sessionId: string,
    private ev: TransportEvents,
    private debug = false,
  ) {}

  connect(): void {
    if (this.closed) return;
    let url: string;
    try {
      const u = new URL(this.endpoint);
      u.protocol = u.protocol === "https:" ? "wss:" : "ws:";
      u.pathname = "/ws";
      u.searchParams.set("role", "visitor");
      if (!this.subprotocol) u.searchParams.set("sessionId", this.sessionId);
      url = u.toString();
    } catch {
      this.ev.onFail("network");
      return;
    }

    let opened = false;
    try {
      // The session token travels as a WebSocket SUBPROTOCOL, not in the query string.
      // URLs are logged by default — CDN access logs, reverse proxies, WAFs, monitoring
      // — and this token is a password scoped to one journey. `Sec-WebSocket-Protocol`
      // is a request header, and headers are not logged by default.
      //
      // `cobrowse.v1` is offered alongside it because RFC 6455 requires the server to
      // accept one of the offered protocols, and the one it names comes back in a
      // response header: that must be the marker, never the credential.
      this.ws = this.subprotocol
        ? new WebSocket(url, ["cobrowse.v1", `cbt.${this.sessionId}`])
        : new WebSocket(url);
    } catch { this.ev.onFail("network"); return; }

    this.ws.onopen = () => {
      opened = true; this.attempts = 0;
      if (this.debug) console.info("[cobrowse] connected");
      this.ping = window.setInterval(() => this.send({ t: "ping" }), PING_MS);
      this.ev.onOpen();
    };
    this.ws.onmessage = (e) => {
      let m: Inbound;
      try { m = JSON.parse(String(e.data)); } catch { return; }
      if (m?.t === "pong") return;
      if (m?.t === "error") {
        // The service says this session is not valid. Retrying cannot help.
        this.closed = true;
        this.ev.onFail("rejected");
        return;
      }
      this.ev.onMessage(m);
    };
    this.ws.onclose = () => {
      window.clearInterval(this.ping);
      if (this.closed) return;
      if (!opened) {
        // First, rule out "the server is older than this SDK". A handshake that never
        // opened while we were offering a subprotocol may mean nothing worse than a
        // deployment that does not know about them yet. Retry immediately with the
        // token in the query string — no backoff, because this is a capability probe,
        // not a network fault, and the customer is waiting.
        if (this.subprotocol) {
          this.subprotocol = false;
          if (this.debug) console.info("[cobrowse] server did not accept the subprotocol; falling back");
          this.connect();
          return;
        }
        this.attempts += 1;
        // A handshake that never opened is ambiguous: the browser WebSocket API does
        // not expose the HTTP status, so a rejected token and a dropped network look
        // identical here. Ask over HTTP, once, before spending the retry ladder on a
        // session the service has already refused.
        if (this.attempts === 1) { void this.classify(); return; }
      } else {
        // A connection we had, and lost. The session is still valid and we are about to
        // retry, so this is degraded rather than over — the distinction the host needs
        // in order to tell the assistant it has gone blind without ending the call.
        this.ev.onDrop?.();
      }
      if (this.attempts >= MAX_ATTEMPTS) { this.ev.onFail("network"); return; }
      const delay = Math.min(1000 * 2 ** this.attempts, MAX_BACKOFF_MS);
      setTimeout(() => this.connect(), delay);
    };
    this.ws.onerror = () => { /* close handler owns recovery */ };
  }

  /**
   * Decide whether the failed handshake was a dead session or a dead network, then
   * either give up or resume the normal backoff.
   *
   * A 401/404 is the service saying this token will never work — retrying is pure noise
   * and leaves the customer staring at nothing for half a minute. Anything else,
   * including the probe itself failing, is treated as a network problem: when in doubt
   * we retry, because falsely killing a live session is the worse error.
   */
  private async classify(): Promise<void> {
    let dead = false;
    try {
      const u = new URL(this.endpoint);
      u.pathname = `/v1/sessions/${encodeURIComponent(this.sessionId)}`;
      u.search = "";
      const r = await fetch(u.toString(), { method: "GET", cache: "no-store" });
      dead = r.status === 401 || r.status === 403 || r.status === 404;
    } catch { dead = false; }

    if (this.closed) return;
    if (dead) {
      this.closed = true;
      if (this.debug) console.info("[cobrowse] session rejected by the service; not retrying");
      this.ev.onFail("rejected");
      return;
    }
    setTimeout(() => this.connect(), Math.min(1000 * 2 ** this.attempts, MAX_BACKOFF_MS));
  }

  send(payload: unknown): void {
    // readyState 1 === OPEN. Compared numerically on purpose: host pages (and monitoring
    // tools) commonly wrap window.WebSocket, and a wrapper that forgets to copy the static
    // constants would make `WebSocket.OPEN` undefined and silently mute every send.
    if (this.ws && this.ws.readyState === 1) {
      try { this.ws.send(JSON.stringify(payload)); } catch { /* dropped frame is not fatal */ }
    }
  }

  close(): void {
    this.closed = true;
    window.clearInterval(this.ping);
    try { this.ws?.close(); } catch { /* already closed */ }
    this.ws = undefined;
  }
}
